import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DomainRoundedIcon from "@mui/icons-material/DomainRounded";
import ExploreRoundedIcon from "@mui/icons-material/ExploreRounded";
import FmdGoodRoundedIcon from "@mui/icons-material/FmdGoodRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { can } from "../../../auth/can";
import { APP_API_BASE } from "../../../config/api";
import {
  deleteRequest,
  getErrorMessage,
  getRequest,
  patchRequest,
  postRequest,
  PROJECTS_API,
} from "./api";
import {
  actionButtonSx,
  PageShell,
  panelSx,
  statusChip,
} from "./ProjectsCommon";
import { Close, SearchRounded } from "@mui/icons-material";

const emptyForm = {
  customer_id: "",
  project_id: "",
  name: "",
  code: "",
  status: "PLANNED",
  city: "",
  state: "",
  address: "",
  latitude: "",
  longitude: "",
  planned_devices: "",
  commissioned_devices: "",
  site_owner: "",
  notes: "",
};

const SITE_STATUS = [
  "PLANNED",
  "READY",
  "IN_PROGRESS",
  "LIVE",
  "ON_HOLD",
  "BLOCKED",
  "COMPLETED",
];
const EXECUTION_READY_STATUSES = new Set([
  "READY",
  "IN_PROGRESS",
  "LIVE",
  "COMPLETED",
]);

const dialogFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: "#F8FAFC",
  },
};

function progressFor(site) {
  const planned = Number(site.planned_devices || 0);
  const commissioned = Number(site.commissioned_devices || 0);
  if (!planned) return 0;
  return Math.min(100, Math.round((commissioned / planned) * 100));
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function MetricCard({ icon, title, value, caption, tone = "#0F172A" }) {
  return (
    <Card elevation={0} sx={{ ...panelSx, height: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.4} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.5,
              display: "grid",
              placeItems: "center",
              color: tone,
              backgroundColor: "rgba(15,23,42,0.05)",
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 900,
                color: "#64748B",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: 28,
                fontWeight: 950,
                color: "#0F172A",
                letterSpacing: "-0.04em",
              }}
            >
              {value}
            </Typography>
          </Box>
        </Stack>
        {caption ? (
          <Typography
            sx={{ mt: 1.2, color: "#64748B", fontSize: 13, lineHeight: 1.55 }}
          >
            {caption}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SitesSkeleton() {
  const metricSkeletons = Array.from({ length: 4 });
  const siteSkeletons = Array.from({ length: 3 });
  const statSkeletons = Array.from({ length: 3 });
  const linkSkeletons = Array.from({ length: 3 });

  return (
    <Stack spacing={2.25}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr 1fr",
          },
          gap: 2,
          width: "100%",
        }}
      >
        {metricSkeletons.map((_, index) => (
          <Box key={index} sx={{ width: "100%" }}>
            <Card elevation={0} sx={{ ...panelSx, height: "100%" }}>
              <CardContent sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.4} alignItems="center">
                  <Skeleton
                    variant="rounded"
                    width={42}
                    height={42}
                    sx={{ borderRadius: 2.5 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="48%" height={18} />
                    <Skeleton variant="text" width="36%" height={42} />
                  </Box>
                </Stack>
                <Skeleton
                  variant="text"
                  width="84%"
                  height={22}
                  sx={{ mt: 1.2 }}
                />
                <Skeleton variant="text" width="62%" height={20} />
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="32%" height={30} />
            <Skeleton variant="text" width="64%" height={22} />
          </Box>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
            <Skeleton
              variant="rounded"
              width={240}
              height={40}
              sx={{ maxWidth: "100%" }}
            />
            <Skeleton
              variant="rounded"
              width={180}
              height={40}
              sx={{ maxWidth: "100%" }}
            />
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {siteSkeletons.map((_, index) => (
          <Box key={index} sx={{ width: "100%" }}>
            <Paper elevation={0} sx={{ ...panelSx, overflow: "hidden" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    lg: "2fr 1fr",
                  },
                }}
              >
                <Box sx={{ p: 2.25 }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    spacing={1.5}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        flexWrap="wrap"
                      >
                        <Skeleton variant="text" width="36%" height={34} />
                        <Skeleton
                          variant="rounded"
                          width={98}
                          height={26}
                          sx={{ borderRadius: 999 }}
                        />
                      </Stack>
                      <Skeleton variant="text" width="52%" height={22} />
                    </Box>
                    <Skeleton
                      variant="rounded"
                      width={126}
                      height={36}
                      sx={{ borderRadius: 2 }}
                    />
                  </Stack>

                  <Box
                    sx={{
                      mt: 1.4,
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(3, 1fr)",
                      },
                      gap: 1.4,
                    }}
                  >
                    {statSkeletons.map((_, statIndex) => (
                      <Box
                        key={statIndex}
                        sx={{
                          p: 1.35,
                          borderRadius: 2,
                          backgroundColor: "#F8FAFC",
                          border: "1px solid #E2E8F0",
                        }}
                      >
                        <Skeleton variant="text" width="46%" height={18} />
                        <Skeleton
                          variant="text"
                          width="28%"
                          height={24}
                          sx={{ mt: 0.4 }}
                        />
                      </Box>
                    ))}
                  </Box>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mt: 1.5 }}
                  >
                    <Skeleton variant="text" width="48%" height={20} />
                    <Skeleton variant="text" width={42} height={20} />
                  </Stack>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={8}
                    sx={{ mt: 0.8, borderRadius: 999 }}
                  />

                  <Divider sx={{ my: 1.7 }} />
                  <Skeleton variant="text" width="92%" height={22} />
                  <Skeleton variant="text" width="68%" height={22} />
                </Box>

                <Box
                  sx={{
                    height: "100%",
                    p: 2,
                    background:
                      "linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)",
                    borderLeft: { lg: "1px solid #E2E8F0" },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack spacing={1.1}>
                    <Skeleton variant="text" width="44%" height={18} />
                    <Skeleton variant="text" width="66%" height={26} />
                    <Skeleton variant="text" width="74%" height={22} />
                    {linkSkeletons.map((_, linkIndex) => (
                      <Skeleton
                        key={linkIndex}
                        variant="rounded"
                        width="100%"
                        height={58}
                        sx={{ borderRadius: 2.5 }}
                      />
                    ))}
                  </Stack>

                  <Stack direction="row" spacing={1.1} sx={{ mt: 2 }}>
                    <Skeleton
                      variant="rounded"
                      width="100%"
                      height={38}
                      sx={{ borderRadius: 2 }}
                    />
                    <Skeleton
                      variant="rounded"
                      width="100%"
                      height={38}
                      sx={{ borderRadius: 2 }}
                    />
                  </Stack>
                </Box>
              </Box>
            </Paper>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

function FormSection({ eyebrow, title, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid #E2E8F0",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 900,
          color: "#64748B",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        sx={{
          mt: 0.35,
          mb: 1.6,
          fontSize: 17,
          fontWeight: 900,
          color: "#0F172A",
          letterSpacing: "-0.03em",
        }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function ModuleLinkCard({ icon, label, caption, onClick }) {
  return (
    <Button
      fullWidth
      onClick={onClick}
      endIcon={<ArrowOutwardRoundedIcon />}
      sx={{
        justifyContent: "space-between",
        px: 1.4,
        py: 1.25,
        borderRadius: 2.5,
        border: "1px solid #E2E8F0",
        backgroundColor: "#FFFFFF",
        color: "#0F172A",
        textTransform: "none",
        "&:hover": {
          backgroundColor: "#F8FAFC",
          borderColor: "#CBD5E1",
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.1}
        alignItems="center"
        sx={{ textAlign: "left" }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            backgroundColor: "rgba(37,99,235,0.08)",
            color: "#2563EB",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#0F172A" }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#64748B" }}>
            {caption}
          </Typography>
        </Box>
      </Stack>
    </Button>
  );
}

export default function ProjectsSites() {
  const { activeOrg, permissions } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const queryParams = new URLSearchParams(location.search);
  const queryProjectId = queryParams.get("project_id") || "";
  const querySiteId = queryParams.get("site_id") || "";
  const querySiteName = queryParams.get("site_name") || "";

  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sitePendingDelete, setSitePendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const isPlatformAdmin = can(permissions, "system.manage");

  const validate = () => {
    const newErrors = {};

    if (!form.name) newErrors.name = "Required";
    if (!form.code) newErrors.code = "Required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const loadData = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    setLoading(true);
    setError("");
    try {
      const [siteData, customerData, projectData] = await Promise.all([
        getRequest(
          queryProjectId
            ? `${PROJECTS_API}/sites?project_id=${queryProjectId}`
            : `${PROJECTS_API}/sites?project_id=__none__`,
          token,
          activeOrg,
        ),
        getRequest(`${PROJECTS_API}/customers`, token, activeOrg),
        getRequest(`${PROJECTS_API}/deployments`, token, activeOrg),
      ]);
      setSites(siteData?.sites || []);
      setCustomers(customerData?.customers || []);
      setProjects(projectData?.projects || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load deployment sites"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg, queryProjectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!error && !success) return undefined;

    const timer = window.setTimeout(() => {
      setError("");
      setSuccess("");
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [error, success]);

  const filteredProjects = projects.filter((project) =>
    form.customer_id ? project.customer_id === form.customer_id : true,
  );

  const visibleSites = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sites.filter((site) => {
      if (querySiteId && String(site.id) !== querySiteId) return false;
      if (
        querySiteName &&
        site.name?.toLowerCase() !== querySiteName.toLowerCase()
      ) {
        return false;
      }
      if (statusFilter !== "ALL" && site.status !== statusFilter) return false;
      if (!q) return true;
      const haystack = [
        site.name,
        site.code,
        site.city,
        site.state,
        site.site_owner,
        site.customer?.name,
        site.project?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [sites, statusFilter, search, querySiteId, querySiteName]);

  const selectedProject = projects.find(
    (project) => String(project.id) === queryProjectId,
  );
  const hasProjectContext = Boolean(queryProjectId);

  const routeProjectName = queryParams.get("project_name") || "";

  const showBackToProject = Boolean(queryProjectId);
  const headerProjectName = selectedProject?.name || routeProjectName;

  const metrics = useMemo(() => {
    const planned = visibleSites.reduce(
      (sum, site) => sum + Number(site.planned_devices || 0),
      0,
    );
    const commissioned = visibleSites.reduce(
      (sum, site) => sum + Number(site.commissioned_devices || 0),
      0,
    );
    const ready = visibleSites.filter((site) =>
      EXECUTION_READY_STATUSES.has(site.status),
    ).length;
    const blocked = visibleSites.filter(
      (site) => site.status === "BLOCKED",
    ).length;
    const progress = planned ? Math.round((commissioned / planned) * 100) : 0;
    return { planned, commissioned, ready, blocked, progress };
  }, [visibleSites]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      customer_id: selectedProject?.customer_id || "",
      project_id: queryProjectId,
    });
    setErrors({});
    setOpen(true);
  };

  const handleEdit = (site) => {
    setEditingId(site.id);
    setForm({
      customer_id: site.customer_id || "",
      project_id: site.project_id || "",
      name: site.name || "",
      code: site.code || "",
      status: site.status || "PLANNED",
      city: site.city || "",
      state: site.state || "",
      address: site.address || "",
      latitude: site.latitude || "",
      longitude: site.longitude || "",
      planned_devices: site.planned_devices || "",
      commissioned_devices: site.commissioned_devices || "",
      site_owner: site.site_owner || "",
      notes: site.notes || "",
    });
    setErrors({});
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);

    const payload = {
      ...form,
      planned_devices: Number(form.planned_devices || 0),
      commissioned_devices: Number(form.commissioned_devices || 0),
      latitude: form.latitude === "" ? null : Number(form.latitude),
      longitude: form.longitude === "" ? null : Number(form.longitude),
    };

    try {
      if (editingId) {
        await patchRequest(
          `${PROJECTS_API}/sites/${editingId}`,
          payload,
          token,
          activeOrg,
        );
        setSuccess("Site updated");
      } else {
        await postRequest(`${PROJECTS_API}/sites`, payload, token, activeOrg);
        setSuccess("Site created");
      }
      setOpen(false);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to save site"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (site) => {
    if (!site?.id) return;
    try {
      setDeleting(true);
      await deleteRequest(`${PROJECTS_API}/sites/${site.id}`, token, activeOrg);
      setSuccess("Site deleted");
      setDeleteDialogOpen(false);
      setSitePendingDelete(null);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to delete site"));
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadCommissioningReport = async (report) => {
    try {
      const response = await axios.get(
        `${APP_API_BASE}/api/v1/commissioning/records/${report.id}/report.pdf`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
          },
        },
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `commissioning-report-${report.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to download commissioning report"));
    }
  };

  const handleReviewCommissioning = async (report, action) => {
    const actionLabel = action.toLowerCase();
    const notes = window.prompt(
      `${actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)} this commissioning record.\nAdd an optional note for audit trail:`,
      "",
    );
    if (notes === null) return;

    try {
      await axios.patch(
        `${APP_API_BASE}/api/v1/commissioning/records/${report.id}/review`,
        {
          action,
          notes,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
          },
        },
      );
      setSuccess(`Commissioning record ${actionLabel}d successfully`);
      await loadData();
    } catch (err) {
      setError(
        getErrorMessage(err, `Unable to ${actionLabel} commissioning record`),
      );
    }
  };

  return (
    <PageShell
      title={
        hasProjectContext
          ? `${headerProjectName || "Project"} Sites`
          : "Deployment Sites"
      }
      subtitle={
        hasProjectContext
          ? "Control site readiness, execution ownership, location context, and planned device quantities for this project before mobile commissioning begins."
          : "Sites are managed inside a project. Open a project dashboard and then manage its sites."
      }
      leadingAction={
        showBackToProject ? (
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "#DBEAFE",
              fontWeight: 800,
              textTransform: "none",
              px: 0,
              minWidth: "auto",
            }}
          >
            Back to Project
          </Button>
        ) : null
      }
      actions={
        hasProjectContext ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={actionButtonSx}
          >
            Add Site
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() =>
              navigate("/projects/deployments", {
                state: { fromSites: true },
              })
            }
            sx={actionButtonSx}
          >
            Open Projects
          </Button>
        )
      }
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}

      {!queryProjectId ? (
        <Paper elevation={0} sx={{ ...panelSx, p: 5, textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 950,
              color: "#0F172A",
              letterSpacing: "-0.04em",
            }}
          >
            Select a project to manage sites
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: "#64748B",
              maxWidth: 680,
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            Deployment sites are execution units under a specific project. They
            are no longer shown as a global all-sites list so rollout progress
            stays project-scoped and auditable.
          </Typography>
          <Button
            variant="contained"
            onClick={() =>
              navigate("/projects/deployments", {
                state: { fromSites: true },
              })
            }
            sx={{ mt: 2.5, fontWeight: 850 }}
          >
            Go to Projects
          </Button>
        </Paper>
      ) : loading ? (
        <SitesSkeleton />
      ) : (
        <Stack spacing={2.25}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr", // 1 column (mobile)
                sm: "1fr 1fr", // 2 columns (tablet)
                md: "1fr 1fr 1fr 1fr", // 4 columns (desktop)
              },
              gap: 2,
              width: "100%",
            }}
          >
            <Box sx={{ width: "100%" }}>
              <MetricCard
                icon={<DomainRoundedIcon />}
                title="Sites"
                value={visibleSites.length}
                caption="Execution locations in current view"
                tone="#2563EB"
              />
            </Box>

            <Box sx={{ width: "100%" }}>
              <MetricCard
                icon={<TaskAltRoundedIcon />}
                title="Ready Sites"
                value={metrics.ready}
                caption="Ready, active, live, or completed"
                tone="#16A34A"
              />
            </Box>

            <Box sx={{ width: "100%" }}>
              <MetricCard
                icon={<ExploreRoundedIcon />}
                title="Devices"
                value={`${metrics.commissioned}/${metrics.planned}`}
                caption={`${metrics.progress}% commissioned against plan`}
                tone="#0F766E"
              />
            </Box>

            <Box sx={{ width: "100%" }}>
              <MetricCard
                icon={<WarningAmberRoundedIcon />}
                title="Blocked"
                value={metrics.blocked}
                caption="Sites requiring escalation"
                tone="#DC2626"
              />
            </Box>
          </Box>

          <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", lg: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  sx={{ fontSize: 18, fontWeight: 900, color: "#0F172A" }}
                >
                  Site Control Board
                </Typography>
                <Typography sx={{ mt: 0.35, color: "#64748B", fontSize: 13 }}>
                  Project-scoped site readiness, ownership, site code, and
                  location controls.
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
                <TextField
                  size="small"
                  placeholder="Search sites"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRounded fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch("")}>
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: { md: 240 } }}
                />
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ minWidth: { md: 180 } }}
                >
                  <MenuItem value="ALL">All Statuses</MenuItem>
                  {SITE_STATUS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.replace(/_/g, " ")}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Stack>
          </Paper>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {visibleSites.map((site) => {
              const progress = progressFor(site);
              const hasCoordinates =
                site.latitude !== null &&
                site.latitude !== undefined &&
                site.longitude !== null &&
                site.longitude !== undefined;
              const reports = Array.isArray(site.commissioning_reports)
                ? site.commissioning_reports
                : [];
              return (
                <Box sx={{ width: "100%" }} key={site.id}>
                  <Paper elevation={0} sx={{ ...panelSx, overflow: "hidden" }}>
                    {/* main section */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          lg: "2fr 1fr",
                        },
                      }}
                    >
                      {/* left Section */}
                      <Box>
                        <Box sx={{ p: 2.25 }}>
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            justifyContent="space-between"
                            spacing={1.5}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                flexWrap="wrap"
                              >
                                <Typography
                                  sx={{
                                    fontSize: 22,
                                    fontWeight: 950,
                                    color: "#0F172A",
                                    letterSpacing: "-0.035em",
                                  }}
                                >
                                  {site.name}
                                </Typography>
                                {statusChip(site.status)}
                              </Stack>
                              <Typography
                                sx={{
                                  mt: 0.45,
                                  fontSize: 13,
                                  color: "#64748B",
                                }}
                              >
                                {site.project?.name || "Project pending"} /{" "}
                                {site.customer?.name || "Customer pending"} /{" "}
                                {site.code}
                              </Typography>
                            </Box>

                            <Stack direction="row" spacing={1}>
                              <Chip
                                icon={<FmdGoodRoundedIcon />}
                                label={
                                  [site.city, site.state]
                                    .filter(Boolean)
                                    .join(", ") || "Location pending"
                                }
                                sx={{
                                  fontWeight: 700,
                                  backgroundColor: "#F8FAFC",
                                }}
                              />
                            </Stack>
                          </Stack>
                          {/* inner stats */}
                          <Box
                            sx={{
                              mt: 1.4,
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                md: "repeat(3, 1fr)",
                              },
                              gap: 1.4,
                            }}
                          >
                            <Box
                              sx={{
                                p: 1.35,
                                borderRadius: 2,
                                backgroundColor: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: "#64748B",
                                  fontWeight: 900,
                                  textTransform: "uppercase",
                                }}
                              >
                                Site Owner
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 0.4,
                                  fontWeight: 800,
                                  color: "#0F172A",
                                }}
                              >
                                {site.site_owner || "Not assigned"}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                p: 1.35,
                                borderRadius: 2,
                                backgroundColor: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: "#64748B",
                                  fontWeight: 900,
                                  textTransform: "uppercase",
                                }}
                              >
                                Planned Devices
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 0.4,
                                  fontWeight: 800,
                                  color: "#0F172A",
                                }}
                              >
                                {site.planned_devices || 0}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                p: 1.35,
                                borderRadius: 2,
                                backgroundColor: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: "#64748B",
                                  fontWeight: 900,
                                  textTransform: "uppercase",
                                }}
                              >
                                Commissioned
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 0.4,
                                  fontWeight: 800,
                                  color: "#0F172A",
                                }}
                              >
                                {site.commissioned_devices || 0}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                p: 1.35,
                                borderRadius: 2,
                                backgroundColor: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: "#64748B",
                                  fontWeight: 900,
                                  textTransform: "uppercase",
                                }}
                              >
                                Survey
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 0.4,
                                  fontWeight: 800,
                                  color: "#0F172A",
                                }}
                              >
                                {String(
                                  site.survey?.status || "NOT_REQUIRED",
                                ).replace(/_/g, " ")}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ mt: 1.5 }}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                            >
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: "#64748B",
                                  fontWeight: 800,
                                }}
                              >
                                Commissioning progress
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: "#0F172A",
                                  fontWeight: 900,
                                }}
                              >
                                {progress}%
                              </Typography>
                            </Stack>

                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              sx={{
                                mt: 0.7,
                                height: 8,
                                borderRadius: 999,
                                backgroundColor: "#E2E8F0",
                                "& .MuiLinearProgress-bar": {
                                  backgroundColor:
                                    progress >= 100 ? "#16A34A" : "#2563EB",
                                },
                              }}
                            />
                          </Box>

                          <Divider sx={{ my: 1.6 }} />
                          <Typography
                            sx={{
                              color: "#475569",
                              fontSize: 13.5,
                              lineHeight: 1.65,
                            }}
                          >
                            {site.address ||
                              "No detailed address captured yet."}
                          </Typography>

                          {hasCoordinates ? (
                            <Typography
                              sx={{ mt: 0.6, color: "#64748B", fontSize: 12.5 }}
                            >
                              GPS: {site.latitude}, {site.longitude}
                            </Typography>
                          ) : null}

                          {Array.isArray(site.commissioning_reports) &&
                          site.commissioning_reports.length ? (
                            <>
                              <Divider sx={{ my: 1.8 }} />
                              <Box
                                sx={{
                                  maxHeight: 280,
                                  overflowY: "auto",
                                  pr: 1,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    color: "#64748B",
                                    fontWeight: 900,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                  }}
                                >
                                  Installation Reports
                                </Typography>
                                <Typography
                                  sx={{
                                    mt: 0.45,
                                    color: "#475569",
                                    fontSize: 13,
                                  }}
                                >
                                  Completed commissioning records captured for
                                  this site.
                                </Typography>
                                <Stack spacing={1.2} sx={{ mt: 1.4 }}>
                                  {site.commissioning_reports.map(
                                    (report, reportIndex) => (
                                      <Box
                                        key={report.id}
                                        sx={{
                                          p: 1.6,
                                          borderRadius: 3,
                                          border: "1px solid #E2E8F0",
                                          background:
                                            "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
                                        }}
                                      >
                                        <Stack
                                          direction={{
                                            xs: "column",
                                            md: "row",
                                          }}
                                          justifyContent="space-between"
                                          spacing={1.1}
                                        >
                                          <Box>
                                            <Stack
                                              direction="row"
                                              spacing={1}
                                              alignItems="center"
                                              flexWrap="wrap"
                                              useFlexGap
                                            >
                                              <Chip
                                                label={`#${String(reportIndex + 1).padStart(2, "0")}`}
                                                size="small"
                                                sx={{
                                                  fontWeight: 900,
                                                  backgroundColor: "#EFF6FF",
                                                  color: "#1D4ED8",
                                                }}
                                              />
                                              <Typography
                                                sx={{
                                                  fontSize: 14,
                                                  fontWeight: 900,
                                                  color: "#0F172A",
                                                }}
                                              >
                                                {report.device?.name ||
                                                  "Commissioned device"}
                                              </Typography>
                                            </Stack>
                                            <Typography
                                              sx={{
                                                mt: 0.35,
                                                fontSize: 12.5,
                                                color: "#64748B",
                                              }}
                                            >
                                              Asset{" "}
                                              {report.inventory_asset
                                                ?.serial_number || "-"}{" "}
                                              /{" "}
                                              {report.provisioning_mode || "-"}
                                            </Typography>
                                          </Box>
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            flexWrap="wrap"
                                          >
                                            {statusChip(
                                              report.commissioning_status,
                                            )}
                                            <Chip
                                              label={formatDateTime(
                                                report.completed_at ||
                                                  report.created_at,
                                              )}
                                              size="small"
                                              sx={{
                                                fontWeight: 800,
                                                backgroundColor: "#F8FAFC",
                                              }}
                                            />
                                          </Stack>
                                        </Stack>

                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          flexWrap="wrap"
                                          useFlexGap
                                          sx={{ mt: 1.1 }}
                                        >
                                          {report.device?.id ? (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              onClick={() =>
                                                navigate(
                                                  `/devices/${report.device.id}`,
                                                )
                                              }
                                              sx={{
                                                fontWeight: 800,
                                                textTransform: "none",
                                              }}
                                            >
                                              Open Device
                                            </Button>
                                          ) : null}
                                          {report.inventory_asset?.id ? (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              onClick={() =>
                                                navigate(
                                                  `/inventory/assets/${report.inventory_asset.id}`,
                                                )
                                              }
                                              sx={{
                                                fontWeight: 800,
                                                textTransform: "none",
                                              }}
                                            >
                                              Open Asset
                                            </Button>
                                          ) : null}
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => navigate("/reports")}
                                            sx={{
                                              fontWeight: 800,
                                              textTransform: "none",
                                            }}
                                          >
                                            Open Reports
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={
                                              <PictureAsPdfRoundedIcon />
                                            }
                                            onClick={() =>
                                              handleDownloadCommissioningReport(
                                                report,
                                              )
                                            }
                                            sx={{
                                              fontWeight: 800,
                                              textTransform: "none",
                                            }}
                                          >
                                            Download PDF
                                          </Button>
                                          {isPlatformAdmin &&
                                          String(
                                            report.commissioning_status || "",
                                          ).toUpperCase() ===
                                            "PENDING_APPROVAL" ? (
                                            <>
                                              <Button
                                                size="small"
                                                color="success"
                                                variant="contained"
                                                onClick={() =>
                                                  handleReviewCommissioning(
                                                    report,
                                                    "APPROVE",
                                                  )
                                                }
                                                sx={{
                                                  fontWeight: 800,
                                                  textTransform: "none",
                                                }}
                                              >
                                                Approve
                                              </Button>
                                              <Button
                                                size="small"
                                                color="warning"
                                                variant="outlined"
                                                onClick={() =>
                                                  handleReviewCommissioning(
                                                    report,
                                                    "REJECT",
                                                  )
                                                }
                                                sx={{
                                                  fontWeight: 800,
                                                  textTransform: "none",
                                                }}
                                              >
                                                Reject
                                              </Button>
                                            </>
                                          ) : null}
                                          {isPlatformAdmin &&
                                          ["APPROVED", "COMPLETED"].includes(
                                            String(
                                              report.commissioning_status || "",
                                            ).toUpperCase(),
                                          ) ? (
                                            <Button
                                              size="small"
                                              color="error"
                                              variant="outlined"
                                              onClick={() =>
                                                handleReviewCommissioning(
                                                  report,
                                                  "REVOKE",
                                                )
                                              }
                                              sx={{
                                                fontWeight: 800,
                                                textTransform: "none",
                                              }}
                                            >
                                              Revoke
                                            </Button>
                                          ) : null}
                                        </Stack>

                                        <Grid
                                          container
                                          spacing={1.2}
                                          sx={{ mt: 0.8 }}
                                        >
                                          <Grid item xs={12} md={4}>
                                            <Typography
                                              sx={{
                                                fontSize: 11,
                                                color: "#64748B",
                                                fontWeight: 900,
                                                textTransform: "uppercase",
                                              }}
                                            >
                                              Installed By
                                            </Typography>
                                            <Typography
                                              sx={{
                                                mt: 0.3,
                                                color: "#0F172A",
                                                fontWeight: 800,
                                              }}
                                            >
                                              {report.installed_by?.name ||
                                                report.installed_by?.email ||
                                                "-"}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={12} md={4}>
                                            <Typography
                                              sx={{
                                                fontSize: 11,
                                                color: "#64748B",
                                                fontWeight: 900,
                                                textTransform: "uppercase",
                                              }}
                                            >
                                              Location Label
                                            </Typography>
                                            <Typography
                                              sx={{
                                                mt: 0.3,
                                                color: "#0F172A",
                                                fontWeight: 800,
                                              }}
                                            >
                                              {report.installation
                                                ?.location_label || "-"}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={12} md={4}>
                                            <Typography
                                              sx={{
                                                fontSize: 11,
                                                color: "#64748B",
                                                fontWeight: 900,
                                                textTransform: "uppercase",
                                              }}
                                            >
                                              Verification
                                            </Typography>
                                            <Typography
                                              sx={{
                                                mt: 0.3,
                                                color: "#0F172A",
                                                fontWeight: 800,
                                              }}
                                            >
                                              {report.verification
                                                ?.device_acknowledged
                                                ? "Acknowledged"
                                                : report.verification
                                                      ?.config_pushed
                                                  ? "Config pushed"
                                                  : "Pending"}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={12} md={4}>
                                            <Typography
                                              sx={{
                                                fontSize: 11,
                                                color: "#64748B",
                                                fontWeight: 900,
                                                textTransform: "uppercase",
                                              }}
                                            >
                                              Review Status
                                            </Typography>
                                            <Typography
                                              sx={{
                                                mt: 0.3,
                                                color: "#0F172A",
                                                fontWeight: 800,
                                              }}
                                            >
                                              {String(
                                                report.verification
                                                  ?.review_status ||
                                                  report.commissioning_status ||
                                                  "-",
                                              ).replace(/_/g, " ")}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={12} md={4}>
                                            <Typography
                                              sx={{
                                                fontSize: 11,
                                                color: "#64748B",
                                                fontWeight: 900,
                                                textTransform: "uppercase",
                                              }}
                                            >
                                              Coordinates
                                            </Typography>
                                            <Typography
                                              sx={{
                                                mt: 0.3,
                                                color: "#0F172A",
                                                fontWeight: 800,
                                              }}
                                            >
                                              {report.installation?.latitude ??
                                                "-"}
                                              ,{" "}
                                              {report.installation?.longitude ??
                                                "-"}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={12} md={8}>
                                            <Typography
                                              sx={{
                                                fontSize: 11,
                                                color: "#64748B",
                                                fontWeight: 900,
                                                textTransform: "uppercase",
                                              }}
                                            >
                                              Notes
                                            </Typography>
                                            <Typography
                                              sx={{
                                                mt: 0.3,
                                                color: "#475569",
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {report.installation?.notes ||
                                                site.notes ||
                                                "No additional installation notes captured."}
                                            </Typography>
                                          </Grid>
                                          {report.verification?.review_notes ? (
                                            <Grid item xs={12}>
                                              <Typography
                                                sx={{
                                                  fontSize: 11,
                                                  color: "#64748B",
                                                  fontWeight: 900,
                                                  textTransform: "uppercase",
                                                }}
                                              >
                                                Admin Review Notes
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  mt: 0.3,
                                                  color: "#475569",
                                                  lineHeight: 1.6,
                                                }}
                                              >
                                                {
                                                  report.verification
                                                    .review_notes
                                                }
                                              </Typography>
                                            </Grid>
                                          ) : null}
                                          {Array.isArray(
                                            report.evidence?.photos,
                                          ) && report.evidence.photos.length ? (
                                            <Grid item xs={12}>
                                              <Typography
                                                sx={{
                                                  fontSize: 11,
                                                  color: "#64748B",
                                                  fontWeight: 900,
                                                  textTransform: "uppercase",
                                                }}
                                              >
                                                Photo Evidence
                                              </Typography>
                                              <Stack
                                                direction="row"
                                                spacing={1.1}
                                                flexWrap="wrap"
                                                useFlexGap
                                                sx={{ mt: 0.9 }}
                                              >
                                                {report.evidence.photos.map(
                                                  (photo, photoIndex) => (
                                                    <Box
                                                      key={`${photo.file_url || "report-photo"}-${photoIndex}`}
                                                      sx={{
                                                        width: {
                                                          xs: "100%",
                                                          sm: 180,
                                                        },
                                                        p: 1,
                                                        borderRadius: 2.5,
                                                        border:
                                                          "1px solid #E2E8F0",
                                                        backgroundColor: "#FFF",
                                                      }}
                                                    >
                                                      {(
                                                        photo.download_url ||
                                                        photo.file_url
                                                      )?.startsWith("http") ? (
                                                        <Box
                                                          component="img"
                                                          src={
                                                            photo.download_url ||
                                                            photo.file_url
                                                          }
                                                          alt={
                                                            photo.label ||
                                                            `Installation photo ${photoIndex + 1}`
                                                          }
                                                          sx={{
                                                            width: "100%",
                                                            height: 110,
                                                            objectFit: "cover",
                                                            borderRadius: 2,
                                                            backgroundColor:
                                                              "#E2E8F0",
                                                          }}
                                                        />
                                                      ) : null}
                                                      <Typography
                                                        sx={{
                                                          mt: 0.8,
                                                          fontSize: 12.5,
                                                          fontWeight: 800,
                                                          color: "#0F172A",
                                                        }}
                                                      >
                                                        {photo.label ||
                                                          `Photo ${photoIndex + 1}`}
                                                      </Typography>
                                                      <Button
                                                        size="small"
                                                        startIcon={
                                                          <DownloadRoundedIcon />
                                                        }
                                                        onClick={() =>
                                                          window.open(
                                                            photo.download_url ||
                                                              photo.file_url,
                                                            "_blank",
                                                            "noopener,noreferrer",
                                                          )
                                                        }
                                                        sx={{
                                                          mt: 0.6,
                                                          px: 0,
                                                          minWidth: 0,
                                                          fontWeight: 800,
                                                          textTransform: "none",
                                                        }}
                                                      >
                                                        Download media
                                                      </Button>
                                                    </Box>
                                                  ),
                                                )}
                                              </Stack>
                                            </Grid>
                                          ) : null}
                                        </Grid>
                                      </Box>
                                    ),
                                  )}
                                </Stack>
                              </Box>
                            </>
                          ) : null}
                        </Box>
                      </Box>
                      {/* right section */}
                      <Grid item xs={12} lg={4}>
                        <Box
                          sx={{
                            height: "100%",
                            p: 2,
                            background:
                              "linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)",
                            borderLeft: { lg: "1px solid #E2E8F0" },
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "#64748B",
                                fontWeight: 900,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                              }}
                            >
                              Operations Hub
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.75,
                                color: "#0F172A",
                                fontWeight: 800,
                              }}
                            >
                              {EXECUTION_READY_STATUSES.has(site.status)
                                ? "Ready for commissioning workflow"
                                : "Preparation pending before mobile commissioning"}
                            </Typography>

                            <Grid container spacing={1.1} sx={{ mt: 1.5 }}>
                              <Grid item xs={4}>
                                <Box
                                  sx={{
                                    p: 1.2,
                                    borderRadius: 2.5,
                                    backgroundColor: "#FFFFFF",
                                    border: "1px solid #E2E8F0",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 10.5,
                                      color: "#64748B",
                                      fontWeight: 900,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Devices
                                  </Typography>
                                  <Typography
                                    sx={{
                                      mt: 0.45,
                                      fontSize: 18,
                                      fontWeight: 900,
                                      color: "#0F172A",
                                    }}
                                  >
                                    {site.commissioned_devices || 0}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={4}>
                                <Box
                                  sx={{
                                    p: 1.2,
                                    borderRadius: 2.5,
                                    backgroundColor: "#FFFFFF",
                                    border: "1px solid #E2E8F0",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 10.5,
                                      color: "#64748B",
                                      fontWeight: 900,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Reports
                                  </Typography>
                                  <Typography
                                    sx={{
                                      mt: 0.45,
                                      fontSize: 18,
                                      fontWeight: 900,
                                      color: "#0F172A",
                                    }}
                                  >
                                    {reports.length}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={4}>
                                <Box
                                  sx={{
                                    p: 1.2,
                                    borderRadius: 2.5,
                                    backgroundColor: "#FFFFFF",
                                    border: "1px solid #E2E8F0",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 10.5,
                                      color: "#64748B",
                                      fontWeight: 900,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Progress
                                  </Typography>
                                  <Typography
                                    sx={{
                                      mt: 0.45,
                                      fontSize: 18,
                                      fontWeight: 900,
                                      color: "#0F172A",
                                    }}
                                  >
                                    {progress}%
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                            <Stack spacing={1.1} sx={{ mt: 1.6 }}>
                              <ModuleLinkCard
                                icon={<DomainRoundedIcon fontSize="small" />}
                                label="Project Dashboard"
                                caption="Review rollout status and project KPIs"
                                onClick={() => navigate(`/projects/overview`)}
                              />
                              <ModuleLinkCard
                                icon={<MemoryRoundedIcon fontSize="small" />}
                                label="Devices Module"
                                caption={`${reports.length} commissioned devices linked here`}
                                onClick={() => {
                                  const firstDeviceId = reports[0]?.device?.id;
                                  if (firstDeviceId) {
                                    navigate(`/devices/${firstDeviceId}`);
                                    return;
                                  }
                                  navigate("/device_onboarding");
                                }}
                              />
                              <ModuleLinkCard
                                icon={
                                  <Inventory2RoundedIcon fontSize="small" />
                                }
                                label="Inventory Asset"
                                caption="Open the most recent commissioned asset"
                                onClick={() => {
                                  const firstAssetId =
                                    reports[0]?.inventory_asset?.id;
                                  if (firstAssetId) {
                                    navigate(
                                      `/inventory/assets/${firstAssetId}`,
                                    );
                                    return;
                                  }
                                  navigate("/inventory/assets");
                                }}
                              />
                              <ModuleLinkCard
                                icon={
                                  <AssessmentRoundedIcon fontSize="small" />
                                }
                                label="Site Surveys"
                                caption={
                                  site.survey?.required
                                    ? `Survey ${String(
                                        site.survey?.status || "required",
                                      )
                                        .replace(/_/g, " ")
                                        .toLowerCase()}`
                                    : "Configure or review site survey"
                                }
                                onClick={() =>
                                  navigate(
                                    `/projects/surveys?project_id=${site.project_id}&site_id=${site.id}`,
                                    {
                                      state: {
                                        fromSitesSurvey: true,
                                        projectName:
                                          site.project?.name ||
                                          selectedProject?.name ||
                                          "",
                                        siteName: site.name || "",
                                      },
                                    },
                                  )
                                }
                              />
                              <ModuleLinkCard
                                icon={
                                  <AssessmentRoundedIcon fontSize="small" />
                                }
                                label="Reports Module"
                                caption="Generate site-level operational reporting"
                                onClick={() => navigate("/reports")}
                              />
                            </Stack>
                            {site.notes ? (
                              <Box
                                sx={{
                                  mt: 1.6,
                                  p: 1.35,
                                  borderRadius: 2.5,
                                  backgroundColor: "#FFFFFF",
                                  border: "1px solid #E2E8F0",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    color: "#64748B",
                                    fontWeight: 900,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Site Notes
                                </Typography>
                                <Typography
                                  sx={{
                                    mt: 0.6,
                                    color: "#475569",
                                    fontSize: 13,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  {site.notes}
                                </Typography>
                              </Box>
                            ) : null}
                          </Box>

                          <Stack direction="row" spacing={1.1} sx={{ mt: 2 }}>
                            <Button
                              fullWidth
                              variant="contained"
                              startIcon={<EditRoundedIcon />}
                              onClick={() => handleEdit(site)}
                              sx={{ fontWeight: 850, textTransform: "none" }}
                            >
                              Edit Site
                            </Button>

                            <Button
                              fullWidth
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteOutlineRoundedIcon />}
                              onClick={() => {
                                setSitePendingDelete(site);
                                setDeleteDialogOpen(true);
                              }}
                              sx={{ fontWeight: 850, textTransform: "none" }}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Box>
                      </Grid>
                    </Box>
                  </Paper>
                </Box>
              );
            })}

            {!visibleSites.length ? (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{ ...panelSx, p: 5, textAlign: "center" }}
                >
                  <Typography
                    sx={{ fontSize: 20, fontWeight: 900, color: "#0F172A" }}
                  >
                    No sites match this view
                  </Typography>
                  <Typography sx={{ mt: 1, color: "#64748B" }}>
                    Adjust filters or add a new deployment site for this
                    project.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                    sx={{ mt: 2, fontWeight: 800 }}
                  >
                    Add Site
                  </Button>
                </Paper>
              </Grid>
            ) : null}
          </Box>
        </Stack>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            background: "#F1F5F9",
            boxShadow: "0 28px 90px rgba(15,23,42,0.38)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.4,
            color: "#F8FAFC",
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#BFDBFE",
                }}
              >
                Site commissioning setup
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: { xs: 24, md: 28 },
                  fontWeight: 700,
                  letterSpacing: "-0.045em",
                }}
              >
                {editingId ? "Edit Deployment Site" : "Create Deployment Site"}
              </Typography>
              <Typography
                sx={{
                  mt: 0.75,
                  color: "rgba(226,232,240,0.82)",
                  fontSize: 13.5,
                }}
              >
                Capture location, ownership, readiness, and device plan before
                field teams start mobile commissioning.
              </Typography>
            </Box>
            <Button
              onClick={() => setOpen(false)}
              startIcon={<CloseRoundedIcon />}
              sx={{ color: "#DBEAFE", fontWeight: 800, textTransform: "none" }}
            ></Button>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.2}>
              <Grid item xs={12} lg={8}>
                <Stack spacing={2.2}>
                  <FormSection eyebrow="Scope" title="Project Binding">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        "& > div": {
                          flex: {
                            xs: "1 1 100%",
                            sm: "1 1 calc(50% - 8px)",
                            md: "1 1 calc(25% - 12px)",
                          },
                        },
                      }}
                    >
                      <Box>
                        <TextField
                          select
                          fullWidth
                          label="Customer"
                          value={form.customer_id}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              customer_id: e.target.value,
                              project_id: "",
                            }))
                          }
                          sx={dialogFieldSx}
                          disabled={Boolean(queryProjectId)}
                        >
                          {customers.map((customer) => (
                            <MenuItem key={customer.id} value={customer.id}>
                              {customer.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                      <Box>
                        <TextField
                          select
                          fullWidth
                          label="Project"
                          value={form.project_id}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              project_id: e.target.value,
                            }))
                          }
                          sx={dialogFieldSx}
                          disabled={Boolean(queryProjectId)}
                        >
                          {filteredProjects.map((project) => (
                            <MenuItem key={project.id} value={project.id}>
                              {project.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </Box>
                  </FormSection>

                  <FormSection
                    eyebrow="Identity"
                    title="Site Identity and Readiness"
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        "& > div": {
                          flex: {
                            xs: "1 1 100%",
                            sm: "1 1 calc(50% - 8px)",
                            md: "1 1 calc(25% - 12px)",
                          },
                        },
                      }}
                    >
                      <Box>
                        <TextField
                          fullWidth
                          required
                          label="Site Name"
                          value={form.name}
                          onChange={(e) => {
                            setForm((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              name: error,
                            }));
                          }}
                          error={!!errors.name}
                          helperText={errors.name}
                          sx={dialogFieldSx}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          required
                          label="Site Code"
                          value={form.code}
                          onChange={(e) => {
                            setForm((prev) => ({
                              ...prev,
                              code: e.target.value.toUpperCase(),
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              code: error,
                            }));
                          }}
                          error={!!errors.code}
                          helperText={errors.code}
                          sx={dialogFieldSx}
                        />
                      </Box>
                      <Box>
                        <TextField
                          select
                          fullWidth
                          label="Status"
                          value={form.status}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              status: e.target.value,
                            }))
                          }
                          sx={dialogFieldSx}
                        >
                          {SITE_STATUS.map((status) => (
                            <MenuItem key={status} value={status}>
                              {status.replace(/_/g, " ")}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </Box>
                  </FormSection>

                  <FormSection
                    eyebrow="Location"
                    title="Address and Coordinates"
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        "& > div": {
                          flex: {
                            xs: "1 1 100%",
                            sm: "1 1 calc(50% - 8px)",
                            md: "1 1 calc(25% - 12px)",
                          },
                        },
                      }}
                    >
                      <Box>
                        <TextField
                          fullWidth
                          label="City"
                          value={form.city}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              city: e.target.value,
                            }))
                          }
                          sx={dialogFieldSx}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="State"
                          value={form.state}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              state: e.target.value,
                            }))
                          }
                          sx={dialogFieldSx}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="Site Owner / Lead"
                          value={form.site_owner}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              site_owner: e.target.value,
                            }))
                          }
                          sx={dialogFieldSx}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="Address"
                          value={form.address}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              address: e.target.value,
                            }))
                          }
                          sx={dialogFieldSx}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          type="number"
                          label="Latitude"
                          value={form.latitude}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              latitude: e.target.value,
                            }))
                          }
                          sx={dialogFieldSx}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          type="number"
                          label="Longitude"
                          value={form.longitude}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              longitude: e.target.value,
                            }))
                          }
                          sx={dialogFieldSx}
                        />
                      </Box>
                    </Box>
                  </FormSection>
                </Stack>
              </Grid>

              <Box
                sx={{
                  width: "100%",
                }}
              >
                <Stack spacing={2.2}>
                  <FormSection
                    eyebrow="Device Plan"
                    title="Commissioning Target"
                  >
                    <Stack spacing={1.6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Planned Devices"
                        value={form.planned_devices}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            planned_devices: e.target.value,
                          }))
                        }
                        sx={dialogFieldSx}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Commissioned Devices"
                        value={form.commissioned_devices}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            commissioned_devices: e.target.value,
                          }))
                        }
                        sx={dialogFieldSx}
                      />
                    </Stack>
                  </FormSection>

                  <FormSection eyebrow="Field Notes" title="Execution Notes">
                    <TextField
                      fullWidth
                      multiline
                      minRows={7}
                      label="Notes"
                      value={form.notes}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, notes: e.target.value }))
                      }
                      sx={dialogFieldSx}
                    />
                  </FormSection>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      background:
                        "linear-gradient(180deg, #ECFEFF 0%, #EFF6FF 100%)",
                      border: "1px solid #BAE6FD",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 900,
                        color: "#0369A1",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}
                    >
                      Field readiness
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.8,
                        color: "#0F172A",
                        fontWeight: 800,
                        lineHeight: 1.55,
                      }}
                    >
                      Sites created here become the mobile commissioning context
                      for operators during zero-touch deployment.
                    </Typography>
                  </Paper>
                </Stack>
              </Box>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "#FFFFFF",
            borderTop: "1px solid #E2E8F0",
          }}
        >
          <Button
            onClick={() => !saving && setOpen(false)}
            disabled={saving}
            sx={{ fontWeight: 800, textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{
              px: 3,
              fontWeight: 900,
              textTransform: "none",
              borderRadius: 2,
              background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
              position: "relative",
              minWidth: 140,
            }}
          >
            {saving ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : editingId ? (
              "Update Site"
            ) : (
              "Create Site"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Delete Site</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{sitePendingDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={async () => {
              setDeleting(true);
              try {
                await handleDelete(sitePendingDelete);
                setDeleteDialogOpen(false);
                setSitePendingDelete(null);
              } catch (err) {
                console.error(err);
              } finally {
                setDeleting(false);
              }
            }}
            sx={{ position: "relative", minWidth: 120 }}
          >
            {deleting ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
